import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from "react";
import en from "./en.json";
import bg from "./bg.json";

type Lang = "en" | "bg";

const translations: Record<Lang, Record<string, any>> = { en, bg };

interface LanguageContextType {
  lang: Lang;
  setLang: (lang: Lang) => void;
  t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

function getNestedValue(obj: any, path: string): string {
  const parts = path.split(".");
  let current = obj;
  for (const part of parts) {
    if (current == null) return path;
    current = current[part];
  }
  return typeof current === "string" ? current : path;
}

function detectLang(): Lang {
  const saved = localStorage.getItem("zephr-lang") as Lang | null;
  if (saved === "en" || saved === "bg") return saved;
  
  const browserLangs = navigator.languages || [navigator.language];
  for (const l of browserLangs) {
    if (l.startsWith("bg")) return "bg";
  }
  return "en";
}

export const LanguageProvider = ({ children, initialLang }: { children: ReactNode; initialLang?: Lang }) => {
  const [lang, setLangState] = useState<Lang>(initialLang ?? detectLang());

  const setLang = useCallback((newLang: Lang) => {
    setLangState(newLang);
    localStorage.setItem("zephr-lang", newLang);
    document.documentElement.lang = newLang;
  }, []);

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  const t = useCallback((key: string): string => {
    return getNestedValue(translations[lang], key);
  }, [lang]);

  return (
    <LanguageContext.Provider value={{ lang, setLang, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useTranslation = () => {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error("useTranslation must be used within LanguageProvider");
  return ctx;
};
